import React, { useState, useCallback } from 'react';
import InputMask from 'react-input-mask';
import Fetch from './../../../functions/Fetch';
import { FormFeedback, FormGroup, Label, Input, Row, Col } from 'reactstrap';
import { getLatLng } from '../../../functions/Maps';
import CustomMap from '../../Map/CustomMap';
import { useKeysStore } from '../../../../../../../core/store/useKeysStore';

const Address = React.forwardRef((props, ref) => {
    const { keys } = useKeysStore();
    const [state, setState] = React.useState({
        index: 1,
        value: props.value,
        latlng: props.value.lat
            ? { lat: props.value.lat, lng: props.value.lng }
            : { lat: -23.5505, lng: -46.6333 },
        changed: props.error ? props.error : false
    });

    const [disabledAddress, setDisabledAddress] = useState(
        !state.value.zipcode
    );

    const [lastUpdateId, setLastUpdateId] = useState();

    const updateFormFieldFetchingApi = useCallback(async ({ name, value }) => {
        let form = state.value;
        form[name] = value;

        let responseLatLng = await getLatLng(form, keys?.gmaps_key);
        form['lat'] = responseLatLng.lat;
        form['lng'] = responseLatLng.lng;

        setState((prevState) => ({
            ...prevState,
            latlng: responseLatLng,
            value: { ...form, ...responseLatLng },
            changed: true
        }));

        props.onChange({
            target: {
                name: props.name,
                value: form
            }
        });
    }, [state.value, keys?.gmaps_key, props]);
    
    const handleChange = useCallback(
        async (element) => {
            let form = state.value;

            if (!element.lat) {
                let name = element.target.name;
                let value = element.target.value;

                if (name === 'zipcode') {
                    form.zipcode = value;
                    setState((prevState) => ({ ...prevState, form }));

                    if (value.length === 9) {
                        let result = await checkZipCode(value);
                        if (result.status) {
                            form.street_name = result.logradouro;
                            form.district = result.bairro;
                            form.city = result.localidade;
                            form.state = result.uf;
                            form.country = 'Brasil';
                            ref && ref.current && ref.current.focus();

                            let responseLatLng = await getLatLng(
                                state.value,
                                keys?.gmaps_key
                            );

                            setState({ latlng: responseLatLng });
                            form['lat'] = responseLatLng.lat;
                            form['lng'] = responseLatLng.lng;

                            setState((prevState) => ({
                                value: { ...form, ...responseLatLng },
                                changed: true
                            }));
                            setDisabledAddress(false);
                        }
                    }
                } else {
                    if (lastUpdateId) {
                        clearTimeout(lastUpdateId);
                    }

                    form[name] = value;
                    setState((prevState) => ({
                        ...prevState,
                        value: { ...form },
                        changed: true
                    }));

                    const updateId = setTimeout(
                        async () =>
                            updateFormFieldFetchingApi({
                                name,
                                value
                            }),
                        1000
                    );

                    setLastUpdateId(updateId);
                }
            } else {
                form['lat'] = element.lat;
                form['lng'] = element.lng;
                form['address_number'] =
                    element.addressInfo?.address_number || '';
                form['street_name'] = element.addressInfo?.street_name || '';
                form['district'] = element.addressInfo?.district || '';
                form['city'] = element.addressInfo?.city || '';
                form['state'] = element.addressInfo?.state || '';
                form['zipcode'] = element.addressInfo?.zipcode || '';
                form['country'] = 'Brasil';
            }

            props.onChange({
                target: {
                    name: props.name,
                    value: form
                }
            });
        },
        [
            state.value,
            keys?.gmaps_key,
            props,
            lastUpdateId,
            ref,
            updateFormFieldFetchingApi
        ]
    );

    const checkZipCode = async (zipcode) => {
        let result = { status: 0 };
        if (zipcode && zipcode.length === 9) {
            let url = `https://viacep.com.br/ws/${zipcode}/json/`;
            result = { status: 1, ...(await Fetch.get(url)) };
        }
        return result;
    };

    return (
        <div ref={ref}>
            <Row form>
                <Col md={3}>
                    <FormGroup>
                        <Label>CEP</Label>
                        {props.required && <span className="required"> *</span>}
                        <Input
                            type="text"
                            mask="99999-999"
                            maskChar=""
                            tag={InputMask}
                            autoComplete="false"
                            name="zipcode"
                            onChange={handleChange}
                            value={state.value.zipcode}
                            invalid={
                                state.changed &&
                                props.required &&
                                !state.value.zipcode
                            }
                            readOnly={props.readOnly}
                            autoFocus={props.autoFocus}
                        />
                        <FormFeedback>O campo é obrigatório</FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={7}>
                    <FormGroup>
                        <Label>Endereço</Label>
                        {props.required && <span className="required"> *</span>}
                        <Input
                            type="text"
                            name="street_name"
                            onChange={handleChange}
                            value={state.value.street_name}
                            invalid={
                                state.changed &&
                                props.required &&
                                !state.value.street_name
                            }
                            readOnly={props.readOnly}
                            disabled={disabledAddress}
                        />
                        <FormFeedback>O campo é obrigatório</FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={2}>
                    <FormGroup>
                        <Label>Número</Label>
                        {props.required && <span className="required"> *</span>}
                        <Input
                            type="text"
                            name="address_number"
                            onChange={handleChange}
                            value={state.value.address_number}
                            invalid={
                                state.changed &&
                                props.required &&
                                !state.value.address_number
                            }
                            readOnly={props.readOnly}
                            disabled={disabledAddress}
                        />
                        <FormFeedback>O campo é obrigatório</FormFeedback>
                    </FormGroup>
                </Col>
            </Row>
            <Row form>
                <Col md={6}>
                    <FormGroup>
                        <Label>Complemento</Label>
                        <Input
                            type="text"
                            name="complement"
                            onChange={handleChange}
                            value={state.value.complement}
                            readOnly={props.readOnly}
                            disabled={disabledAddress}
                        />
                        <FormFeedback>O campo é obrigatório</FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label>Bairro</Label>
                        {props.required && <span className="required"> *</span>}
                        <Input
                            type="text"
                            name="district"
                            onChange={handleChange}
                            value={state.value.district}
                            invalid={
                                state.changed &&
                                props.required &&
                                !state.value.district
                            }
                            readOnly={props.readOnly}
                            disabled={disabledAddress}
                        />
                        <FormFeedback>O campo é obrigatório</FormFeedback>
                    </FormGroup>
                </Col>
            </Row>
            <Row form>
                <Col md={9}>
                    <FormGroup>
                        <Label>Cidade</Label>
                        {props.required && <span className="required"> *</span>}
                        <Input
                            type="text"
                            name="city"
                            onChange={handleChange}
                            value={state.value.city}
                            invalid={
                                state.changed &&
                                props.required &&
                                !state.value.city
                            }
                            readOnly={props.readOnly}
                            disabled={disabledAddress}
                        />
                        <FormFeedback>O campo é obrigatório</FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={3}>
                    <FormGroup>
                        <Label>UF</Label>
                        {props.required && <span className="required"> *</span>}
                        <Input
                            type="text"
                            name="state"
                            onChange={handleChange}
                            value={state.value.state}
                            invalid={
                                state.changed &&
                                props.required &&
                                !state.value.state
                            }
                            readOnly={props.readOnly}
                            disabled={disabledAddress}
                        />
                        <FormFeedback>O campo é obrigatório</FormFeedback>
                    </FormGroup>
                </Col>
            </Row>
            {props.googleMap && (
                <Row>
                    <Col md={12}>
                        <CustomMap
                            markerPosition={state.latlng}
                            setMarkerPosition={(response) => {
                                handleChange(response);
                            }}
                        />
                    </Col>
                </Row>
            )}
        </div>
    );
});

Address.defaultProps = {
    value: {
        zipcode: '',
        country: '',
        state: '',
        city: '',
        district: '',
        street_name: '',
        address_number: '',
        complement: '',
        lat: '',
        lng: ''
    },
    invalid: {},
    error: {},
    handleChange: (element) => {}
};

export default Address;
